import { DisasterWildfire } from "@areahub/icons";
import { getLight } from "@areahub/ui";
import {
  Box,
  ButtonProps,
  Circle,
  Heading,
  HStack,
  IconButton,
  IconButtonProps,
} from "@chakra-ui/react";
import "keen-slider/keen-slider.min.css";
import { ButtonAction } from "lib/gcms/fragment-typings";
import { useState } from "react";
import { FaBiohazard, FaSmog } from "react-icons/fa";
import { IoChevronBackOutline, IoChevronForwardOutline } from "react-icons/io5";
import { mapButton } from "utils/map-button";
import { Carousel, CarouselSlide, useCarousel } from "./carousel-elements";

export const features = [
  {
    icon: DisasterWildfire,
    title: "Find active wildfires across the nation",
    subtitle: (
      <span>
        {" "}
        <p>
          Search for fires near you and across the United States with our
          wildfires portal.
        </p>
        <p>
          Learn the latest fire news available and the steps you can take to
          protect your home from wildfires.
        </p>
      </span>
    ),
    color: "red.500",
    cta: {
      label: "Launch Wildfires Portal",
      buttonAction: ButtonAction.ExternalLink,
      link: "https://wildfires.areahub.com/",
    },
  },
  {
    icon: FaBiohazard,
    title: "Uncover toxic contaminated sites",
    subtitle: (
      <span>
        {" "}
        <p>
          More than 50 million Americans live within a few miles of a hazardous
          waste site.
        </p>
        <p>
          Find out if you are near one and how it can affect your health and
          property.
        </p>
      </span>
    ),
    color: "green.600",
    cta: {
      label: "Get Your Report",
      buttonAction: ButtonAction.OpenSearch,
    },
  },
  {
    icon: FaSmog,
    title: "Explore air quality in your area",
    subtitle: (
      <span>
        {" "}
        <p>
          Breathing polluted air can have serious health effects. Find out the
          average air quality and, even more importantly, the peak levels, in
          your county.
        </p>
      </span>
    ),
    color: "gray.500",
    cta: {
      label: "Get Your Report",
      buttonAction: ButtonAction.OpenSearch,
    },
  },
];

export const Gallery = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoaded, setLoaded] = useState(false);

  // Create a looping carousel with initial slide of currentSlide, and creation/slide change hooks
  const [sliderRef, slider] = useCarousel({
    loop: true,
    slideChanged: (slider) => setCurrentSlide(slider.track.details.rel),
    created: () => setLoaded(true),
  });

  function prevSlide() {
    slider.current?.prev();
  }
  function nextSlide() {
    slider.current?.next();
  }
  function setSlide(idx: number) {
    slider.current?.moveToIdx(idx);
  }

  const sharedArrowStyles: ButtonProps = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
  };

  return (
    <Box mt="30px">
      <Carousel ref={sliderRef} mx="auto" maxW="4xl">
        {features.map(({ icon, title, subtitle, color, cta }, i) => (
          <CarouselSlide
            key={i}
            textAlign="center"
            opacity={currentSlide === i ? 1 : 0.4}
            slideLength={features.length}
            idx={i}
          >
            <Box>
              <Heading as="h2" size="xl" fontWeight="bold">
                {title}
              </Heading>
              <Heading as="h3" size="sm" fontWeight="normal" mt="10px">
                {subtitle}
              </Heading>
            </Box>
            <Box
              as={icon}
              boxSize={{ base: "100px", lg: "200px" }}
              mx="auto"
              fill={color}
              mt={{ base: "30px", lg: "30px" }}
            />
            {mapButton(
              {
                id: `cta-${i}`,
                label: cta?.label,
                href: cta?.link,
                buttonAction: cta?.buttonAction,
              },
              {
                mx: "auto",
                mt: "30px",
                display: "block",
                textColor: "inherit",
                bg: "white",
                borderWidth: "1px",
                _hover: {
                  bg: getLight(color),
                },
              },
            )}
          </CarouselSlide>
        ))}
      </Carousel>
      {isLoaded && (
        <>
          <CarouselIconButton
            {...sharedArrowStyles}
            left="5px"
            onClick={prevSlide}
            icon={<IoChevronBackOutline />}
            aria-label="Previous Slide"
          />
          <CarouselIconButton
            {...sharedArrowStyles}
            right="5px"
            onClick={nextSlide}
            icon={<IoChevronForwardOutline />}
            aria-label="Next Slide"
          />
        </>
      )}
      <HStack
        w="fit-content"
        mx="auto"
        justify="center"
        mt="20px"
        spacing="15px"
      >
        {features.map(({ icon, color }, index) => {
          const isSelected = currentSlide == index;
          const Icon = () => (
            <Box
              as={icon}
              boxSize="16px"
              fill={color}
              onClick={() => setSlide(index)}
              cursor="pointer"
            />
          );
          return isSelected ? (
            <Circle key={index} size="30px" borderWidth="3px">
              <Icon />
            </Circle>
          ) : (
            <Icon key={index} />
          );
        })}
      </HStack>
    </Box>
  );
};

export const CarouselIconButton = (props: IconButtonProps) => (
  <IconButton
    isRound
    boxShadow="base"
    bg="white"
    _hover={{
      bg: "gray.100",
    }}
    _active={{
      bg: "gray.200",
    }}
    _focus={{ boxShadow: "inerhit" }}
    _focusVisible={{ boxShadow: "outline" }}
    {...props}
  />
);
